import React from 'react';

// ─── Bar Chart ─────────────────────────────────────────────────────────────
export function BarChart({ data, height = 200, color = '#CC0000' }) {
  if (!data || !data.length) return <div className="h-40 flex items-center justify-center text-gray-400 text-sm">Tidak ada data</div>;
  const max = Math.max(...data.map(d => d.value), 1);

  return (
    <div className="w-full" style={{ height }}>
      <div className="flex items-end justify-between gap-2 h-full pb-6 relative">
        {data.map((item, i) => (
          <div key={i} className="flex flex-col items-center flex-1 h-full justify-end gap-1">
            <span className="text-xs text-gray-600 font-medium">{item.value}</span>
            <div
              className="w-full rounded-t-md transition-all duration-500"
              style={{ height: `${(item.value / max) * 70}%`, backgroundColor: item.color || color, minHeight: item.value > 0 ? 4 : 0 }}
            />
            <span className="text-xs text-gray-400 text-center truncate w-full px-0.5">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Pie Chart ─────────────────────────────────────────────────────────────
export function PieChart({ data, size = 160 }) {
  if (!data || !data.length) return <div className="flex items-center justify-center text-gray-400 text-sm">Tidak ada data</div>;
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <div className="flex items-center justify-center text-gray-400 text-sm">Tidak ada data</div>;

  let cumulative = 0;
  const segments = data.map(item => {
    const pct = item.value / total;
    const startAngle = cumulative * 360;
    cumulative += pct;
    const endAngle = cumulative * 360;
    return { ...item, startAngle, endAngle, pct };
  });

  const toXY = (angle, r) => {
    const rad = ((angle - 90) * Math.PI) / 180;
    return { x: 80 + r * Math.cos(rad), y: 80 + r * Math.sin(rad) };
  };

  return (
    <div className="flex items-center gap-6">
      <svg width={size} height={size} viewBox="0 0 160 160">
        {segments.map((seg, i) => {
          const start = toXY(seg.startAngle, 70);
          const end = toXY(seg.endAngle, 70);
          const large = seg.endAngle - seg.startAngle > 180 ? 1 : 0;
          const path = `M 80 80 L ${start.x} ${start.y} A 70 70 0 ${large} 1 ${end.x} ${end.y} Z`;
          return <path key={i} d={path} fill={seg.color} stroke="white" strokeWidth="2" />;
        })}
        <circle cx="80" cy="80" r="35" fill="white" />
        <text x="80" y="76" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#374151">{total}</text>
        <text x="80" y="90" textAnchor="middle" fontSize="9" fill="#9CA3AF">Total</text>
      </svg>
      <div className="space-y-2 flex-1">
        {data.map((item, i) => (
          <div key={i} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color }} />
              <span className="text-xs text-gray-600">{item.label}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-700">{item.value}</span>
              <span className="text-xs text-gray-400">({total > 0 ? Math.round((item.value / total) * 100) : 0}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Line Chart ─────────────────────────────────────────────────────────────
export function LineChart({ data, height = 200, color = '#CC0000' }) {
  if (!data || data.length < 2) return <div className="flex items-center justify-center text-gray-400 text-sm h-32">Tidak ada data</div>;

  const W = 400, H = 150;
  const max = Math.max(...data.map(d => d.value), 1);
  const min = Math.min(...data.map(d => d.value), 0);
  const range = max - min || 1;
  const pad = { top: 20, right: 10, bottom: 30, left: 30 };
  const chartW = W - pad.left - pad.right;
  const chartH = H - pad.top - pad.bottom;

  const pts = data.map((d, i) => ({
    x: pad.left + (i / (data.length - 1)) * chartW,
    y: pad.top + chartH - ((d.value - min) / range) * chartH,
    ...d,
  }));

  const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = `${pathD} L ${pts[pts.length-1].x} ${pad.top + chartH} L ${pts[0].x} ${pad.top + chartH} Z`;

  return (
    <div style={{ height }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full">
        <defs>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.2" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
          <line key={i} x1={pad.left} y1={pad.top + t * chartH} x2={pad.left + chartW} y2={pad.top + t * chartH} stroke="#f0f0f0" strokeWidth="1" />
        ))}
        {/* Area */}
        <path d={areaD} fill="url(#lineGrad)" />
        {/* Line */}
        <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Dots */}
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="4" fill="white" stroke={color} strokeWidth="2" />
        ))}
        {/* X Labels */}
        {pts.map((p, i) => (
          <text key={i} x={p.x} y={H - 8} textAnchor="middle" fontSize="9" fill="#9CA3AF">{p.label}</text>
        ))}
        {/* Y labels */}
        {[min, Math.round((min + max) / 2), max].map((v, i) => (
          <text key={i} x={pad.left - 5} y={pad.top + chartH - ((v - min) / range) * chartH + 3} textAnchor="end" fontSize="9" fill="#9CA3AF">{v}</text>
        ))}
      </svg>
    </div>
  );
}

// ─── Progress Bar ──────────────────────────────────────────────────────────
export function ProgressBar({ value, max, color = 'bg-red-600', label, showPercent = true }) {
  const pct = Math.min(Math.round((value / (max || 1)) * 100), 100);
  return (
    <div className="w-full">
      {(label || showPercent) && (
        <div className="flex justify-between items-center mb-1">
          {label && <span className="text-xs text-gray-600">{label}</span>}
          {showPercent && <span className="text-xs font-semibold text-gray-700">{pct}%</span>}
        </div>
      )}
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
